import { useState, useEffect, useRef, useCallback } from "react";
import { setPageName } from "../redux/actions";
import { useDispatch } from "react-redux";
import { goPage } from "../system/coords";
import Logo from "../components/logo";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

const ArrowIcon = "/assets/images/arrow.svg";

const imageCategories = [
  { prefix: "web-apps", count: 5 },
  { prefix: "data-processing", count: 5 },
  { prefix: "cyber-security", count: 5 },
  { prefix: "outro", count: 50 },
];

const categorySpeeds = {
  "web-apps": 600,
  "data-processing": 500,
  "cyber-security": 400,
};

// Outro images (after category images), displaying at every faster speed
const outroSpeeds = {
  start: 300,
  end: 10,
};

const ShowcaseContent = () => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentImage, setCurrentImage] = useState(1);
  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showPreface, setShowPreface] = useState(false);
  const [showPrefaceLogo, setShowPrefaceLogo] = useState(false);
  const [showPrefaceText, setShowPrefaceText] = useState(false);
  const [showEpilogue, setShowEpilogue] = useState(false);
  const [showEpilogueLogo, setShowEpilogueLogo] = useState(false);
  const [showEpilogueText, setShowEpilogueText] = useState(false);
  const [showEpilogueButton, setShowEpilogueButton] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [base64Images, setBase64Images] = useState([]);
  const categoryRef = useRef(0);
  const intervalRef = useRef();
  const timersRef = useRef(new Set());
  const containerRef = useRef();
  const preloadingRef = useRef(false);
  const showcaseImageRef = useRef();

  const clearShowcaseInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const clearShowcaseTimers = useCallback(() => {
    for (const timer of timersRef.current) {
      clearTimeout(timer);
    }
    timersRef.current.clear();
  }, []);

  const scheduleShowcaseTimer = useCallback((callback, delay) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      callback();
    }, delay);

    timersRef.current.add(timer);
    return timer;
  }, []);

  // Preload all images as Image objects with Base64 blob
  useEffect(() => {
    // Prevent double preloading using ref that persists across re-mounts
    if (preloadingRef.current) {
      return;
    }

    preloadingRef.current = true;

    const preloadImages = async () => {
      // 2 second delay before starting to load images, to give some priority to the main content
      await new Promise(resolve => setTimeout(resolve, 2000));
      const imagePromises = [];
      const imageObjects = [];

      for (let categoryIndex = 0; categoryIndex < imageCategories.length; categoryIndex++) {
        const category = imageCategories[categoryIndex];

        for (let i = 1; i <= category.count; i++) {
          const imageUrl = `/assets/images/showcase/${category.prefix}-${i}.jpg`;

          const promise = fetch(imageUrl)
            .then(response => response.blob())
            .then(blob => {
              return new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  // Create an Image object from the Base64 data
                  const img = new Image();
                  img.addEventListener("load", () => {
                    // Store the actual Image object with metadata
                    imageObjects.push({
                      image: img,
                      category: category.prefix,
                      index: i,
                      categoryIndex: categoryIndex,
                    });
                    resolve();
                  });
                  img.src = reader.result; // Set the Base64 data as src
                };
                reader.readAsDataURL(blob);
              });
            })
            .catch(error => {
              console.error(`Failed to preload ${imageUrl}:`, error);
            });

          imagePromises.push(promise);

          // 10ms delay between each image request
          if (categoryIndex < imageCategories.length - 1 || i < category.count) {
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        }
      }

      try {
        await Promise.all(imagePromises);
        setBase64Images(imageObjects);
        setImagesLoaded(true);
      } catch (error) {
        console.error("Some images failed to preload:", error);
        setImagesLoaded(true);
      }
    };

    preloadImages();
  }, []); // Empty dependency array, but ref prevents double execution

  const getCurrentImageSource = () => {
    if (!imagesLoaded || base64Images.length === 0) {
      return "/assets/images/showcase/web-apps-1.jpg"; // Fallback
    }

    const currentPrefix = imageCategories[currentCategory].prefix;
    const imageData = base64Images.find(img => img.category === currentPrefix && img.index === currentImage);

    return imageData ? imageData.image : undefined;
  };

  const currentPrefix = imageCategories[currentCategory].prefix;
  const currentImageObject = getCurrentImageSource();

  const setShowcaseImage = useCallback(
    container => {
      // Clear existing content, clone the preloaded image object and add it to the DOM
      container.innerHTML = "";
      const imgClone = currentImageObject.cloneNode(true);
      imgClone.alt = `${currentPrefix} ${currentImage}`;
      imgClone.style.display = "inline-block";
      container.append(imgClone);
    },
    [currentImageObject, currentPrefix, currentImage],
  );

  // Update the showcase image when currentImage changes
  useEffect(() => {
    if (imagesLoaded && showcaseImageRef.current && currentImageObject && !showPreface && !showEpilogue) {
      const container = showcaseImageRef.current;
      if (container) {
        setShowcaseImage(container);
      }
    }
  }, [
    currentImage,
    currentCategory,
    imagesLoaded,
    showPreface,
    showEpilogue,
    currentImageObject,
    currentPrefix,
    setShowcaseImage,
  ]);

  // Ensure current image is shown when returning in manual mode
  useEffect(() => {
    if (isVisible && isManualMode && imagesLoaded && currentImageObject && !showPreface && !showEpilogue) {
      // We're returning to Showcase in manual mode, ensure the current image is displayed
      const container = showcaseImageRef.current;
      if (container) {
        setShowcaseImage(container);
      }
    }
  }, [
    isVisible,
    isManualMode,
    imagesLoaded,
    currentImageObject,
    currentPrefix,
    currentImage,
    showPreface,
    showEpilogue,
    setShowcaseImage,
  ]);

  // Keep ref in sync with state
  useEffect(() => {
    categoryRef.current = currentCategory;
  }, [currentCategory]);

  const handleVisibilityChange = useCallback(
    isIntersecting => {
      setIsVisible(isIntersecting);
      clearShowcaseTimers();

      // Component is not visible, stop cycling and reset after 1 second
      if (!isIntersecting) {
        clearShowcaseInterval();
        setIsActive(false);
        setShowPreface(true);
        setShowPrefaceLogo(false);
        setShowPrefaceText(false);
        setShowEpilogue(false);
        setShowEpilogueLogo(false);
        setShowEpilogueText(false);
        setShowEpilogueButton(false);
        // Don't reset isManualMode here - preserve it!

        // Only reset image position to web-apps-1 if NOT in manual mode
        if (!isManualMode) {
          scheduleShowcaseTimer(() => {
            setCurrentCategory(0);
            setCurrentImage(1);
            categoryRef.current = 0;
          }, 1000);
        }

        return;
      }

      // Component is visible
      clearShowcaseInterval();

      // If we're in manual mode, don't show preface - just show the current image
      if (isManualMode) {
        setShowPreface(false);
        setShowPrefaceLogo(false);
        setShowPrefaceText(false);
        setShowEpilogue(false);
        setShowEpilogueLogo(false);
        setShowEpilogueText(false);
        setShowEpilogueButton(false);
        return; // Don't start the slideshow flow
      }

      // Component is visible and not in manual mode, start slideshow flow
      scheduleShowcaseTimer(() => {
        setShowPreface(true);
        setShowPrefaceLogo(false); // Don't show logo immediately
        setShowPrefaceText(false);
        setShowEpilogue(false);
        setShowEpilogueLogo(false);
        setShowEpilogueText(false);
        setShowEpilogueButton(false);
        setIsManualMode(false);

        // Show preface logo after 0.8 seconds
        scheduleShowcaseTimer(() => {
          setShowPrefaceLogo(true);
        }, 800);

        // Show preface text after 3 seconds (giving time for logo to appear and slide)
        scheduleShowcaseTimer(() => {
          setShowPrefaceText(true);
        }, 3000);

        // After 4.5 seconds, hide logo and text but keep preface visible for just a little longer
        scheduleShowcaseTimer(() => {
          setShowPrefaceText(false);
          setShowPrefaceLogo(false);
        }, 4500);

        // After 5 seconds total, end preface and start showcase
        scheduleShowcaseTimer(() => {
          setShowPreface(false);
          setIsActive(true);
        }, 5000);
      }, 0);
    },
    [clearShowcaseInterval, clearShowcaseTimers, isManualMode, scheduleShowcaseTimer],
  );

  // Detect when component becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          handleVisibilityChange(entry.isIntersecting);
        }
      },
      { threshold: 0.5 }, // Trigger when 50% of the component is visible
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
      clearShowcaseTimers();
      clearShowcaseInterval();
    };
  }, [clearShowcaseInterval, clearShowcaseTimers, handleVisibilityChange]);

  const startEpilogueSequence = useCallback(() => {
    clearShowcaseTimers();
    setShowEpilogue(true);
    setShowEpilogueLogo(false);
    setShowEpilogueText(false);
    setShowEpilogueButton(false);

    // Wait 1, 1 and 1.8 seconds between showing logo, text and button, waiting for user interaction
    scheduleShowcaseTimer(() => {
      setShowEpilogueLogo(true);
    }, 1000);
    scheduleShowcaseTimer(() => {
      setShowEpilogueText(true);
    }, 2000);
    scheduleShowcaseTimer(() => {
      setShowEpilogueButton(true);
    }, 3800);
  }, [clearShowcaseTimers, scheduleShowcaseTimer]);

  useEffect(() => {
    if (!isActive) return; // Don't start cycling until active

    const getCurrentDelay = () => {
      if (imageCategories[categoryRef.current].prefix === "outro") {
        // Calculate speed based on current outro image
        const progress = (currentImage - 1) / (imageCategories[categoryRef.current].count - 1);
        const delay = outroSpeeds.start - progress * (outroSpeeds.start - outroSpeeds.end);
        return delay;
      }

      // Use individual category speeds
      const currentPrefix = imageCategories[categoryRef.current].prefix;
      return categorySpeeds[currentPrefix] || 1000; // Default fallback
    };

    intervalRef.current = setInterval(() => {
      setCurrentImage(prev => {
        const currentCat = imageCategories[categoryRef.current];
        const isFinalImage = categoryRef.current === imageCategories.length - 1 && prev >= imageCategories.at(-1).count;

        if (isFinalImage) {
          clearShowcaseInterval();
          setIsActive(false);
          startEpilogueSequence();
          return prev;
        }

        if (prev >= currentCat.count) {
          // Move to next category
          const nextCategory = (categoryRef.current + 1) % imageCategories.length;
          setCurrentCategory(nextCategory);
          return 1;
        }
        return prev + 1;
      });
    }, getCurrentDelay());

    return () => {
      clearShowcaseInterval();
    };
  }, [clearShowcaseInterval, currentImage, isActive, startEpilogueSequence]);

  const goToNext = () => {
    if (isManualMode || showEpilogue) {
      if (showEpilogue) {
        setShowEpilogue(false);
        setShowEpilogueLogo(false);
        setShowEpilogueText(false);
        setShowEpilogueButton(false);
        setIsManualMode(true);
        // Reset to first image when transitioning to manual mode
        setCurrentCategory(0);
        setCurrentImage(1);
        return; // Don't advance image when transitioning to manual mode
      }

      // Only advance image if already in manual mode
      if (isManualMode) {
        setCurrentImage(prev => {
          const currentCat = imageCategories[currentCategory];
          if (prev >= currentCat.count) {
            // Move to next category
            const nextCategory = (currentCategory + 1) % imageCategories.length;
            setCurrentCategory(nextCategory);
            return 1;
          }
          return prev + 1;
        });
      }
    }
  };

  const goToPrevious = () => {
    if (isManualMode) {
      setCurrentImage(prev => {
        if (prev <= 1) {
          // Move to previous category
          const prevCategory = currentCategory === 0 ? imageCategories.length - 1 : currentCategory - 1;
          setCurrentCategory(prevCategory);
          return imageCategories[prevCategory].count;
        }
        return prev - 1;
      });
    }
  };

  const renderLogoSection = (sectionName, showSection, showLogo, showText, text, extraContent) => (
    <div
      className="Showcase-logo-section"
      style={{
        display: showSection ? "block" : "none",
      }}
    >
      <div
        className={`Page-logo ${sectionName === "preface" ? "Preface-logo" : ""}`}
        style={{
          display: showLogo ? "block" : "none",
        }}
      >
        <Logo />
      </div>
      <div
        className="Showcase-logo-text"
        style={{
          display: showText ? "block" : "none",
        }}
      >
        {text}
      </div>
      {extraContent}
    </div>
  );

  const dispatch = useDispatch();

  return (
    <>
      <div ref={containerRef} className="Showcase center full-height">
        {renderLogoSection("preface", showPreface, showPrefaceLogo, showPrefaceText, "Some projects worked on...")}

        {renderLogoSection(
          "epilogue",
          showEpilogue,
          showEpilogueLogo,
          showEpilogueText,
          "Building awesome things since the 90's",
          /* Next button during epilogue */
          <button
            className="Showcase-button Epilogue-button"
            style={{
              display: showEpilogueButton ? "block" : "none",
            }}
            onClick={goToNext}
          >
            <NavigateNextIcon sx={{ fontSize: 32 }} />
          </button>,
        )}

        {/* Showcase Image - hidden during logo display */}
        <div
          ref={showcaseImageRef}
          className="Showcase-image-container"
          style={{
            display: showPreface || showEpilogue ? "none" : "block",
          }}
        >
          {/* Preloaded images are inserted here by the useEffect */}
        </div>

        {/* Manual navigation buttons - only visible in manual mode */}
        {isManualMode && (
          <>
            <button
              className="Showcase-button"
              style={{
                left: "50px",
              }}
              onClick={goToPrevious}
            >
              <NavigateBeforeIcon sx={{ fontSize: 32 }} />
            </button>
            <button
              className="Showcase-button"
              style={{
                right: "50px",
              }}
              onClick={goToNext}
            >
              <NavigateNextIcon sx={{ fontSize: 32 }} />
            </button>

            {/* Image counter - shows current position out of total */}
            <div className="Showcase-counter">
              {(() => {
                let totalImages = 0;
                let totalCount = 0;

                for (const imageCategory of imageCategories) {
                  totalCount += imageCategory.count;
                }

                for (let i = 0; i < currentCategory; i++) {
                  totalImages += imageCategories[i].count;
                }
                const currentPosition = totalImages + currentImage;

                return `${currentPosition} of ${totalCount}`;
              })()}
            </div>
          </>
        )}
      </div>
      <img
        src={ArrowIcon}
        className="Arrow-icon Arrow-Showcase"
        alt="Go to next page"
        onClick={() => {
          goPage("Demos");
          dispatch(setPageName("Demos"));
        }}
      />
    </>
  );
};

export default ShowcaseContent;
